/**
 * CharacterLibrary Page
 *
 * Shows all characters created by the child.
 * Entry point to the Character Builder feature.
 */

import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ChevronLeft, Users, Plus, Search, Loader2 } from 'lucide-react'
import { Button, Card, CardContent } from '@/components/ui'
import { CharacterCard } from '@/components/characters'
import { useFamily } from '@/context/FamilyContext'
import { useToast } from '@/context/ToastContext'
import {
  getCharactersForChild,
  toggleFavorite,
  deleteCharacter,
  duplicateCharacter,
  searchCharacters,
} from '@/services/characterLibraryService'
import type { LibraryCharacter } from '@/types/database'

export function CharacterLibrary() {
  const { activeWriter } = useFamily()
  const { showToast } = useToast()

  const [characters, setCharacters] = useState<LibraryCharacter[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')

  // Load characters
  useEffect(() => {
    async function loadData() {
      if (!activeWriter) return

      setIsLoading(true)
      try {
        const data = await getCharactersForChild(activeWriter.id)
        setCharacters(data)
      } catch (error) {
        console.error('Error loading characters:', error)
        showToast('Failed to load characters', 'error')
      } finally {
        setIsLoading(false)
      }
    }

    loadData()
  }, [activeWriter, showToast])

  // Handle search
  useEffect(() => {
    async function search() {
      if (!activeWriter) return

      try {
        if (searchQuery.trim()) {
          const results = await searchCharacters(activeWriter.id, searchQuery)
          setCharacters(results)
        } else {
          const data = await getCharactersForChild(activeWriter.id)
          setCharacters(data)
        }
      } catch (error) {
        console.error('Error searching characters:', error)
      }
    }

    const debounce = setTimeout(search, 300)
    return () => clearTimeout(debounce)
  }, [searchQuery, activeWriter])

  // Handle toggle favorite
  const handleToggleFavorite = async (id: string) => {
    try {
      const updated = await toggleFavorite(id)
      setCharacters(chars =>
        chars.map(c => (c.id === id ? updated : c))
      )
    } catch (error) {
      console.error('Error toggling favorite:', error)
      showToast('Failed to update favorite', 'error')
    }
  }

  // Handle delete
  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this character?')) return

    try {
      await deleteCharacter(id)
      setCharacters(chars => chars.filter(c => c.id !== id))
      showToast('Character deleted', 'success')
    } catch (error) {
      console.error('Error deleting character:', error)
      showToast('Failed to delete character', 'error')
    }
  }

  // Handle duplicate
  const handleDuplicate = async (id: string) => {
    if (!activeWriter) return

    try {
      const duplicated = await duplicateCharacter(id, activeWriter.id)
      setCharacters(chars => [duplicated, ...chars])
      showToast('Character duplicated', 'success')
    } catch (error) {
      console.error('Error duplicating character:', error)
      showToast('Failed to duplicate character', 'error')
    }
  }

  // Separate favorites and regular
  const favoriteCharacters = characters.filter(c => c.is_favorite)
  const regularCharacters = characters.filter(c => !c.is_favorite)

  return (
    <div className="min-h-screen bg-[var(--color-kids-bg)]">
      {/* Header */}
      <header className="bg-[var(--color-kids-surface)] border-b border-[var(--color-kids-surface-alt)] sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Link to="/dashboard">
                <Button variant="ghost" size="sm">
                  <ChevronLeft className="w-5 h-5" />
                </Button>
              </Link>
              <div className="flex items-center gap-2">
                <Users className="w-6 h-6 text-[var(--color-kids-secondary)]" />
                <h1 className="font-display font-bold text-xl text-[var(--color-kids-text)]">
                  My Characters
                </h1>
              </div>
            </div>

            <Link to="/characters/new">
              <Button variant="primary" size="sm">
                <Plus className="w-4 h-4 mr-1" />
                New Character
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-6">
        {/* Search */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--color-kids-text-muted)]" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search characters..."
              className="w-full pl-10 pr-4 py-3 rounded-[var(--radius-kid)] bg-[var(--color-kids-surface)] border-2 border-[var(--color-kids-surface-alt)] text-[var(--color-kids-text)] placeholder:text-[var(--color-kids-text-light)] focus:outline-none focus:border-[var(--color-kids-primary)]"
            />
          </div>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-[var(--color-kids-secondary)]" />
          </div>
        )}

        {/* Empty State */}
        {!isLoading && characters.length === 0 && !searchQuery && (
          <Card>
            <CardContent className="p-8 text-center">
              <Users className="w-16 h-16 mx-auto mb-4 text-[var(--color-kids-text-light)]" />
              <h2 className="font-display font-bold text-xl text-[var(--color-kids-text)] mb-2">
                No Characters Yet
              </h2>
              <p className="text-[var(--color-kids-text-muted)] mb-6">
                Create your first character to use in your stories and challenges!
              </p>
              <Link to="/characters/new">
                <Button variant="primary">
                  <Plus className="w-5 h-5 mr-2" />
                  Create Your First Character
                </Button>
              </Link>
            </CardContent>
          </Card>
        )}

        {/* Search No Results */}
        {!isLoading && characters.length === 0 && searchQuery && (
          <Card>
            <CardContent className="p-8 text-center">
              <Search className="w-12 h-12 mx-auto mb-3 text-[var(--color-kids-text-light)]" />
              <p className="text-[var(--color-kids-text-muted)]">
                No characters found matching "{searchQuery}"
              </p>
            </CardContent>
          </Card>
        )}

        {/* Characters Grid */}
        {!isLoading && characters.length > 0 && (
          <div className="space-y-8">
            {/* Favorites */}
            {favoriteCharacters.length > 0 && (
              <section>
                <h2 className="font-display font-bold text-lg text-[var(--color-kids-text)] mb-4">
                  Favorites
                </h2>
                <div className="grid gap-4 md:grid-cols-2">
                  {favoriteCharacters.map((character, index) => (
                    <motion.div
                      key={character.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <CharacterCard
                        character={character}
                        onToggleFavorite={handleToggleFavorite}
                        onDelete={handleDelete}
                        onDuplicate={handleDuplicate}
                      />
                    </motion.div>
                  ))}
                </div>
              </section>
            )}

            {/* All Characters */}
            {regularCharacters.length > 0 && (
              <section>
                <h2 className="font-display font-bold text-lg text-[var(--color-kids-text)] mb-4">
                  {favoriteCharacters.length > 0 ? 'All Characters' : 'My Characters'}
                </h2>
                <div className="grid gap-4 md:grid-cols-2">
                  {regularCharacters.map((character, index) => (
                    <motion.div
                      key={character.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <CharacterCard
                        character={character}
                        onToggleFavorite={handleToggleFavorite}
                        onDelete={handleDelete}
                        onDuplicate={handleDuplicate}
                      />
                    </motion.div>
                  ))}
                </div>
              </section>
            )}

            {/* Stats */}
            <Card>
              <CardContent className="p-6">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-2xl font-display font-bold text-[var(--color-kids-secondary)]">
                      {characters.length}
                    </p>
                    <p className="text-sm text-[var(--color-kids-text-muted)]">
                      Characters
                    </p>
                  </div>
                  <div>
                    <p className="text-2xl font-display font-bold text-[var(--color-kids-warning)]">
                      {favoriteCharacters.length}
                    </p>
                    <p className="text-sm text-[var(--color-kids-text-muted)]">
                      Favorites
                    </p>
                  </div>
                  <div>
                    <p className="text-2xl font-display font-bold text-[var(--color-kids-primary)]">
                      {characters.reduce((sum, c) => sum + c.stories_used_in, 0)}
                    </p>
                    <p className="text-sm text-[var(--color-kids-text-muted)]">
                      Stories Written
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </main>
    </div>
  )
}

export default CharacterLibrary
